import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { vi } from 'date-fns/locale';

import { getEmployees } from '@/modules/orders/redux/OrderCRUD';

export interface SelectedItemPreviewRow {
  key: string;
  itemCode: string;
  itemName: string;
  exportQty: number;
}

interface EmployeeOption {
  value: string;
  label: string;
  name?: string;
}

interface IssueExportPreviewModalProps {
  open: boolean;
  onClose: () => void;
  items: SelectedItemPreviewRow[];
  onConfirm?: (payload: { picker: EmployeeOption; exportDate: Date; note?: string }) => void | Promise<void>;
  confirmLoading?: boolean;
  confirmError?: string | null;
}

const formatQty = (value: number) => Number(value || 0).toLocaleString('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

export default function IssueExportPreviewModal({
  open,
  onClose,
  items,
  onConfirm,
  confirmLoading = false,
  confirmError = null,
}: IssueExportPreviewModalProps) {
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [selectedPicker, setSelectedPicker] = useState<EmployeeOption | null>(null);
  const [exportDate, setExportDate] = useState<Date | null>(new Date());
  const [note, setNote] = useState<string>('');
  const employeeSearchTimeout = useRef<number | null>(null);

  const loadEmployees = async (search?: string) => {
    setEmployeeLoading(true);
    try {
      const payload = await getEmployees(search || undefined);
      const mapped = (Array.isArray(payload) ? payload : []).map((employee: any) => ({
        value: employee.value,
        label: employee.label,
        name: employee.name,
      }));
      setEmployeeOptions(mapped);
    } catch (_error) {
      setEmployeeOptions([]);
    } finally {
      setEmployeeLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    void loadEmployees('');
  }, [open]);

  useEffect(() => {
    if (open) {
      return;
    }

    setSelectedPicker(null);
    setExportDate(new Date());
    setNote('');
  }, [open]);

  useEffect(() => {
    return () => {
      if (employeeSearchTimeout.current) {
        window.clearTimeout(employeeSearchTimeout.current);
      }
    };
  }, []);

  const handleConfirm = () => {
    if (!selectedPicker || !items.length || !exportDate) {
      return;
    }

    void onConfirm?.({
      picker: selectedPicker,
      exportDate,
      note: note?.trim() ? note.trim() : undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Preview tạo phiếu xuất kho</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 0.5, mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.75 }}>
            Ngày xuất
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
            <DatePicker
              value={exportDate}
              onChange={(date) => setExportDate(date)}
              format='dd/MM/yyyy'
                disabled={confirmLoading}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Box sx={{ mt: 0.5, mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.75 }}>
            Người thực hiện xuất hàng (Picking)
          </Typography>
          <Autocomplete
            options={employeeOptions}
            value={selectedPicker}
            onChange={(_event, value) => setSelectedPicker(value)}
            onInputChange={(_event, value, reason) => {
              if (reason === 'input' || reason === 'clear') {
                if (employeeSearchTimeout.current) {
                  window.clearTimeout(employeeSearchTimeout.current);
                }
                employeeSearchTimeout.current = window.setTimeout(() => {
                  void loadEmployees(value);
                }, 250) as unknown as number;
              }
            }}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            loading={employeeLoading}
            disabled={confirmLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                placeholder='Chọn hoặc tìm nhân viên'
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {employeeLoading ? <CircularProgress color='inherit' size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ mt: 0.5, mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.75 }}>
            Ghi chú
          </Typography>
          <TextField
            size='small'
            fullWidth
            multiline
            minRows={2}
            placeholder='Ghi chú cho phiếu xuất (tuỳ chọn)'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={confirmLoading}
          />
        </Box>

        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.75 }}>
          Danh sách item đã chọn
        </Typography>
        <TableContainer sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Mã vật tư</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tên vật tư</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Số lượng xuất</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length ? items.map((item) => (
                <TableRow key={item.key}>
                  <TableCell>{item.itemCode}</TableCell>
                  <TableCell>{item.itemName}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{formatQty(item.exportQty)}</TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: 'center', color: '#6b7280' }}>Chưa có item được chọn.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {confirmError ? (
          <Typography sx={{ mt: 1.25, color: '#dc2626', fontSize: '0.875rem' }}>
            {confirmError}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirmLoading}>Đóng</Button>
        <Button variant='contained' onClick={handleConfirm} disabled={confirmLoading || !selectedPicker || !items.length || !exportDate}>
          {confirmLoading ? 'Đang tạo...' : 'Xác nhận tạo phiếu'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
